/**
 * PMT Product Carousel — progressive enhancement for sections/pmt-product-carousel.liquid.
 *
 * Two jobs, both optional to the section working:
 *   1. The prev/next arrows. The row is a native scroll-snap container, so it
 *      already scrolls by touch, trackpad and keyboard without this file. The
 *      arrows reveal themselves only once this runs, disable at each end, and
 *      hide entirely when everything already fits.
 *   2. Add to cart. The card ships a real <form action="/cart/add">, so with this
 *      file blocked the button still works — it just lands on /cart. Here it
 *      posts in place and confirms on the button, which is the only confirmation
 *      a shopper gets on this store: layout/theme.liquid's openCartDrawer()
 *      matches no element in the Horizon header and returns false. That same file
 *      patches window.fetch to refresh the cart count after any /cart/add, so
 *      the count is deliberately not touched here.
 *
 * A custom element, not a querySelector IIFE: the product page carries this
 * section twice, and every query below is scoped to the instance. It also
 * re-runs on its own through the theme editor's section re-render, so there is
 * nothing listening for shopify:section:load.
 */

const ADDED_REVERT_MS = 2500;
const ERROR_CLEAR_MS = 10000;

class PmtProductCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.viewport = this.querySelector('[data-pmt-pc-viewport]');
    this.track = this.querySelector('[data-pmt-pc-track]');
    this.nav = this.querySelector('[data-pmt-pc-nav]');
    this.arrows = Array.from(this.querySelectorAll('[data-pmt-pc-arrow]'));
    this.dotsContainer = this.querySelector('[data-pmt-pc-dots]');
    this.timers = new Map();

    if (this.viewport && this.track) {
      this.viewport.addEventListener('scroll', () => this.update(), { passive: true, signal });

      for (const arrow of this.arrows) {
        arrow.addEventListener('click', (event) => this.onArrowClick(event, arrow), { signal });
      }

      if (this.nav && this.arrows.length) {
        this.nav.setAttribute('data-enhanced', '');
      }

      // The row's scrollable width changes as lazy images decode and as the
      // viewport resizes, and both change which arrows should be live.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.viewport);
        this.observer.observe(this.track);
      }

      this.update();
    }

    this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
  }

  /** Read on every use, so a mid-session OS change is honoured. */
  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  get isRtl() {
    return window.getComputedStyle(this).direction === 'rtl';
  }

  update() {
    const viewport = this.viewport;
    if (!viewport) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const scrollable = maxScroll > 1;

    if (this.nav) {
      this.nav.setAttribute('data-scrollable', scrollable ? 'true' : 'false');
    }

    // RTL scroll positions run from 0 down to -maxScroll in current engines.
    const position = Math.abs(viewport.scrollLeft);
    const atStart = !scrollable || position <= 1;
    const atEnd = !scrollable || position >= maxScroll - 1;

    for (const arrow of this.arrows) {
      const disabled = arrow.dataset.pmtPcArrow === 'prev' ? atStart : atEnd;
      arrow.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    this.updateDots();
  }

  /** Update the active dot indicator based on the closest snapped item. */
  updateDots() {
    const dotsContainer = this.dotsContainer;
    if (!dotsContainer || !this.track || !this.viewport) return;

    const dots = Array.from(dotsContainer.querySelectorAll('.pmt-product-carousel__dot'));
    if (!dots.length) return;

    const items = Array.from(this.track.children);
    if (!items.length) return;

    const viewportLeft = this.viewport.getBoundingClientRect().left;
    let activeIndex = 0;
    let minDist = Infinity;

    items.forEach((item, i) => {
      const dist = Math.abs(item.getBoundingClientRect().left - viewportLeft);
      if (dist < minDist) {
        minDist = dist;
        activeIndex = i;
      }
    });

    dots.forEach((dot, i) => {
      dot.classList.toggle('pmt-product-carousel__dot--active', i === activeIndex);
    });
  }

  onArrowClick(event, arrow) {
    event.preventDefault();

    // aria-disabled, not the disabled attribute, so the click is a no-op here
    // rather than the browser dropping focus the moment an end is reached.
    if (arrow.getAttribute('aria-disabled') === 'true') return;

    const viewport = this.viewport;
    const first = this.track?.firstElementChild;
    if (!viewport || !first) return;

    const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    const step = first.getBoundingClientRect().width + gap;
    if (!step) return;

    // One press moves one full page of whatever is currently visible — measured,
    // never hardcoded, because cards per view is a setting and a breakpoint.
    const page = Math.max(1, Math.round(viewport.clientWidth / step));
    const distance = step * page * (arrow.dataset.pmtPcArrow === 'prev' ? -1 : 1);

    viewport.scrollBy({
      left: this.isRtl ? -distance : distance,
      behavior: this.scrollBehavior,
    });
  }

  async onSubmit(event) {
    const form = event.target.closest('[data-pmt-pc-form]');
    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const added = form.querySelector('[data-pmt-pc-added]');
    const error = form.querySelector('[data-pmt-pc-error]');
    const status = form.querySelector('[data-pmt-pc-status]');
    const idle = form.querySelector('[data-pmt-pc-idle]');

    // A second submit while the first is in flight would double the quantity.
    if (!button || button.hasAttribute('data-loading')) return;

    this.clearTimer(form);
    button.setAttribute('data-loading', '');
    form.setAttribute('aria-busy', 'true');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const endpoint = this.dataset.cartAddUrl || '/cart/add';

    try {
      const response = await fetch(endpoint.endsWith('.js') ? endpoint : `${endpoint}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'That item could not be added.');
      }

      button.classList.add('is-added');
      if (idle) idle.hidden = true;
      if (added) added.hidden = false;
      if (status) status.textContent = `${form.dataset.productTitle || 'Item'} added to cart`;

      this.setTimer(form, () => {
        button.classList.remove('is-added');
        if (idle) idle.hidden = false;
        if (added) added.hidden = true;
        if (status) status.textContent = '';
      }, ADDED_REVERT_MS);
    } catch (requestError) {
      if (error) {
        // A TypeError here is the network failing, whose message ("Failed to
        // fetch") means nothing to a shopper.
        error.textContent =
          requestError instanceof TypeError
            ? 'That item could not be added. Please try again.'
            : requestError.message;
        error.hidden = false;
        this.setTimer(form, () => {
          error.hidden = true;
          error.textContent = '';
        }, ERROR_CLEAR_MS);
      }
    } finally {
      button.removeAttribute('data-loading');
      form.removeAttribute('aria-busy');
    }
  }

  setTimer(form, callback, delay) {
    this.clearTimer(form);
    this.timers.set(form, setTimeout(callback, delay));
  }

  clearTimer(form) {
    const timer = this.timers.get(form);
    if (timer) clearTimeout(timer);
    this.timers.delete(form);
  }
}

if (!customElements.get('pmt-product-carousel')) {
  customElements.define('pmt-product-carousel', PmtProductCarousel);
}
